/**
 * Player-facing token panel. Shows the live (stubbed) market cap and the
 * progress bar toward the next mcap-milestone payout — "X SOL of Y SOL to
 * next payout". This is the bar players watch to feel "the next crossing
 * is close", which is the whole point of the milestone bonus existing.
 *
 * Reads two swappable sources:
 *   - `mcapSource`         live mcap (stub today; pump.fun tomorrow)
 *   - `milestoneTargets`   peak + next-threshold (stub today; Season PDA tomorrow)
 *
 * Replacing either does not require touching this file.
 */
import { LAMPORTS_PER_SOL } from "@solana/web3.js";
import React, { useEffect, useState } from "react";

import { DraggableOverlay } from "../util/DraggableOverlay";
import { useIsTouch } from "../util/useIsTouch";
import { mcapSource, type McapSample } from "./mcapSource";
import { milestoneTargets } from "./milestoneTargets";
import { useMilestoneLog } from "./milestoneEvents";

function fmtSolShort(lamports: number): string {
  const sol = lamports / LAMPORTS_PER_SOL;
  if (sol >= 1000) return `${(sol / 1000).toFixed(2)}k`;
  if (sol >= 10) return sol.toFixed(1);
  if (sol >= 1) return sol.toFixed(2);
  return sol.toFixed(3);
}

export function TokenMcapPanel(): JSX.Element {
  const [sample, setSample] = useState<McapSample>(() => mcapSource.sampleNow());
  useEffect(() => mcapSource.subscribe(setSample), []);
  const isTouch = useIsTouch();

  const targets = milestoneTargets.sample(sample.lamports);
  const remaining = Math.max(0, targets.nextThresholdLamports - sample.lamports);
  const denominator = Math.max(1, targets.nextThresholdLamports - targets.peakLamports);
  const numerator = Math.max(0, sample.lamports - targets.peakLamports);
  const progressPct = Math.min(100, Math.max(0, (numerator / denominator) * 100));

  const recent = useMilestoneLog((s) => s.entries.slice(0, 3));
  const isStub = sample.source === "stub" || targets.source === "stub";

  return (
    <DraggableOverlay
      id="token-mcap"
      defaultAnchor={{ top: 12, left: 12, safeAreaTop: isTouch }}
      enabled={isTouch}
      zIndex={9}
    >
      <div style={panelStyle}>
        <div style={headerRowStyle}>
          <div style={titleStyle}>Token mcap</div>
          {isStub && <div style={stubBadgeStyle} title="Stub data until pump.fun feed is wired in">STUB</div>}
        </div>

        <div style={mcapRowStyle}>
          <div style={mcapValueStyle}>{fmtSolShort(sample.lamports)} SOL</div>
          <div style={mcapHintStyle}>posted mcap</div>
        </div>

        <div style={barTrackStyle} aria-label="Progress toward next milestone payout">
          <div style={{ ...barFillStyle, width: `${progressPct}%` }} />
        </div>

        <div style={milestoneRowStyle}>
          <span style={milestoneLabelStyle}>
            {fmtSolShort(remaining)} SOL to next payout
          </span>
          <span style={milestoneTargetStyle}>
            next: {fmtSolShort(targets.nextThresholdLamports)} SOL
          </span>
        </div>

        {recent.length > 0 && (
          <div style={logBlockStyle}>
            <div style={logHeaderStyle}>recent crossings</div>
            {recent.map((e) => (
              <div key={e.id} style={logRowStyle}>
                <span style={logBadgeStyle}>#{e.id}</span>
                <span style={logTextStyle}>
                  {fmtSolShort(e.thresholdLamports)} → {fmtSolShort(e.payoutLamports)} paid
                  {e.winners.length > 0 && ` · ${e.winners.length} winners`}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </DraggableOverlay>
  );
}

const panelStyle: React.CSSProperties = {
  width: 240,
  padding: "10px 12px",
  background: "rgba(8, 8, 13, 0.86)",
  border: "1px solid #2a2a36",
  borderRadius: 8,
  color: "#e8e8ef",
  fontFamily:
    'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
  display: "flex",
  flexDirection: "column",
  gap: 6,
  backdropFilter: "blur(6px)",
  boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
};

const headerRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const titleStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 1.4,
  color: "#9aa0b4",
  textTransform: "uppercase",
};

const stubBadgeStyle: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: 1,
  padding: "1px 5px",
  background: "#3a2a14",
  border: "1px solid #6a4a1c",
  color: "#f5c674",
  borderRadius: 3,
};

const mcapRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: 6,
};

const mcapValueStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 700,
  color: "#e8e8ef",
  fontVariantNumeric: "tabular-nums",
};

const mcapHintStyle: React.CSSProperties = {
  fontSize: 10,
  color: "#5a5a72",
};

const barTrackStyle: React.CSSProperties = {
  height: 8,
  background: "#1c1c26",
  borderRadius: 4,
  overflow: "hidden",
  border: "1px solid #2a2a36",
};

const barFillStyle: React.CSSProperties = {
  height: "100%",
  background: "linear-gradient(90deg, #4a8eff, #7af09e)",
  transition: "width 800ms ease-out",
};

const milestoneRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  fontSize: 11,
};

const milestoneLabelStyle: React.CSSProperties = {
  color: "#cfd2dc",
  fontWeight: 600,
};

const milestoneTargetStyle: React.CSSProperties = {
  color: "#5a5a72",
  fontVariantNumeric: "tabular-nums",
};

const logBlockStyle: React.CSSProperties = {
  marginTop: 4,
  paddingTop: 6,
  borderTop: "1px solid #1c1c26",
  display: "flex",
  flexDirection: "column",
  gap: 3,
};

const logHeaderStyle: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: 1.2,
  color: "#5a5a72",
  textTransform: "uppercase",
};

const logRowStyle: React.CSSProperties = {
  display: "flex",
  gap: 6,
  alignItems: "baseline",
  fontSize: 10,
};

const logBadgeStyle: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 700,
  padding: "1px 4px",
  background: "#1c2433",
  border: "1px solid #2a3a5a",
  color: "#4a8eff",
  borderRadius: 3,
  flex: "0 0 auto",
};

const logTextStyle: React.CSSProperties = {
  color: "#9aa0b4",
  fontVariantNumeric: "tabular-nums",
};
